import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import ReCAPTCHA from "react-google-recaptcha";
import SUBMIT_PARTNER_FORM from "../../../mutations/submitPartnerForm";

interface ContactUsFormProps {
  isRequired: boolean;
  buttonName: string;

  isContactForm?: boolean;
}

const PartnersApplyForm: React.FC<ContactUsFormProps> = ({
  isRequired,
  buttonName,

  isContactForm,
}: any) => {
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] = useState(false);
  const [isPrivacyPolicyCheckedError, setIsPrivacyPolicyCheckedError] =
    useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  if (typeof window === "undefined") {
    return null;
  }

  const [submitContactForm] = useMutation(SUBMIT_PARTNER_FORM);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [message, setMessage] = useState("");

  const validation = (): boolean => {
    let errored = false;

    if (!isPrivacyPolicyChecked) {
      setIsPrivacyPolicyCheckedError(
        "You cannot proceed without accepting our privacy policy"
      );
      errored = true;
    } else {
      setIsPrivacyPolicyCheckedError("");
    }

    if (!recaptchaToken) {
      setRecaptchaError("Please complete the reCAPTCHA.");
      errored = true;
    } else {
      setRecaptchaError("");
    }

    return errored;
  };

  const handleFormData = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validation()) return;

    setIsLoading(true); // Set loading to true
    try {
      const { data } = await submitContactForm({
        variables: {
          fullName,
          email,
          companyName,
          country,
          message,
          recaptchaToken,
        },
      });

      if (data?.submitPartnerApplyForm?.successMessage) {
        toast({
          status: "success",
          title: "Message was sent!",
        });
        setRecaptchaToken(null);
      }
    } catch (error) {
      console.log("Error submitting the form:", error);
      toast({
        title: "Error",
        description: "There was an error submitting the form.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleFormData}
      method="POST"
      id="myForm"
      style={{ width: "100%" }}
    >
      <VStack w="100%" gap={0} mb={{ base: 0, md: 16 }}>
        <SimpleGrid
          w="100%"
          maxW="816px"
          columns={{ base: 1, xl: 2 }}
          spacing={{ base: 3, xl: 5 }}
        >
          <FormControl>
            {isContactForm && (
              <FormLabel
                fontSize="14px"
                color="#6A7471"
                className="regular-text"
              >
                Full Name{" "}
                {isRequired && <span style={{ color: "#116266" }}>*</span>}
              </FormLabel>
            )}
            <Input
              h="48px"
              placeholder={isContactForm ? "" : "Name"}
              required={isRequired}
              type="text"
              borderRadius="full"
              borderColor={
                isContactForm ? "#DADEDC" : "rgba(255, 255, 255, 0.80)"
              }
              bg={isContactForm ? "transparent" : "#fff"}
              focusBorderColor="#116266"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </FormControl>

          <FormControl>
            {isContactForm && (
              <FormLabel
                fontSize="14px"
                color="#6A7471"
                className="regular-text"
              >
                E-mail{" "}
                {isRequired && <span style={{ color: "#116266" }}>*</span>}
              </FormLabel>
            )}
            <Input
              h="48px"
              placeholder={isContactForm ? "" : "Email"}
              required={isRequired}
              type="email"
              bg={isContactForm ? "transparent" : "#fff"}
              borderRadius="full"
              borderColor={
                isContactForm ? "#DADEDC" : "rgba(255, 255, 255, 0.80)"
              }
              focusBorderColor="#116266"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormControl>
        </SimpleGrid>

        <SimpleGrid
          columns={{ base: 1, xl: 2 }}
          spacing={{ base: 3, xl: 5 }}
          w="100%"
        >
          <FormControl mt={5}>
            {isContactForm && (
              <FormLabel
                fontSize="14px"
                color="#6A7471"
                className="regular-text"
              >
                Company / Website / LinkedIn{" "}
                {isRequired && <span style={{ color: "#116266" }}>*</span>}
              </FormLabel>
            )}
            <Input
              h="48px"
              bg={isContactForm ? "transparent" : "#fff"}
              required={isRequired}
              placeholder={isContactForm ? "" : "Company Name"}
              type="text"
              borderRadius="full"
              borderColor={
                isContactForm ? "#DADEDC" : "rgba(255, 255, 255, 0.80)"
              }
              focusBorderColor="#116266"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </FormControl>

          <FormControl mt={5}>
            {isContactForm && (
              <FormLabel
                fontSize="14px"
                color="#6A7471"
                className="regular-text"
              >
                Country of Operation{" "}
                {isRequired && <span style={{ color: "#116266" }}>*</span>}
              </FormLabel>
            )}
            <Input
              h="48px"
              bg={isContactForm ? "transparent" : "#fff"}
              required={isRequired}
              placeholder={isContactForm ? "" : "Company Name"}
              type="text"
              borderRadius="full"
              borderColor={
                isContactForm ? "#DADEDC" : "rgba(255, 255, 255, 0.80)"
              }
              focusBorderColor="#116266"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </FormControl>
        </SimpleGrid>
        <FormControl mt={5}>
          {isContactForm && (
            <FormLabel fontSize="14px" color="#6A7471" className="regular-text">
              Brief Description of Your Audience or Network
              {isRequired && <span style={{ color: "#116266" }}>*</span>}
            </FormLabel>
          )}
          <Textarea
            bg={isContactForm ? "transparent" : "#fff"}
            required={isRequired}
            placeholder={isContactForm ? "" : "Message"}
            borderRadius="16px"
            borderColor={
              isContactForm ? "#DADEDC" : "rgba(255, 255, 255, 0.80)"
            }
            focusBorderColor="#116266"
            minH="80px"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </FormControl>

        <HStack mt={7} w="100%">
          <Checkbox
            as="span"
            isChecked={isPrivacyPolicyChecked}
            onChange={() => setIsPrivacyPolicyChecked(!isPrivacyPolicyChecked)}
            colorScheme="teal"
          >
            <Text
              as="span"
              fontSize="14px"
              color={isContactForm ? "#6A7471" : "#fff"}
            >
              I agree to{" "}
            </Text>

            <span>
              <a href="/privacy-policy" onClick={(e) => e.stopPropagation()}>
                <Text
                  fontSize="14px"
                  textDecoration="underline"
                  as="span"
                  color={isContactForm ? "#116266" : "#fff"}
                >
                  Privacy Policy
                </Text>
              </a>
            </span>
          </Checkbox>
        </HStack>
        <Text w="100%" color="red">
          {isPrivacyPolicyCheckedError}
        </Text>

        <Box w="100%" pt="16px">
          <ReCAPTCHA
            sitekey="6LfNPyQrAAAAACZbM8brJkgHllSclSh_rV97nBsc"
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
          <Text w="100%" color="red">
            {recaptchaError}
          </Text>
        </Box>

        <Button
          mt={5}
          bg={isContactForm ? "#116266" : "#fff"}
          borderRadius="full"
          py={6}
          className="medium-text"
          fontWeight="600"
          fontSize="18px"
          _hover={{ bg: isContactForm && "#4C9195" }}
          color={isContactForm ? "#fff" : "#116266"}
          type="submit"
          isLoading={isLoading}
        >
          {isLoading ? "Loading..." : buttonName}
        </Button>
      </VStack>
    </form>
  );
};

export default PartnersApplyForm;
